import React, { ReactNode } from 'react';
import { useImageFallback } from '@/hooks/useImageFallback';

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallbackSrc?: string;
  fallbackComponent?: ReactNode;
  showPlaceholder?: boolean;
  hideOnError?: boolean;
  retryOnError?: boolean;
  retryDelay?: number;
  maxRetries?: number;
  showLoadingState?: boolean;
  loadingComponent?: ReactNode;
  placeholderClassName?: string;
  containerClassName?: string;
  onLoadSuccess?: () => void;
  onLoadError?: (error: Event) => void;
}

export const Image: React.FC<ImageProps> = ({
  src,
  alt,
  fallbackSrc,
  fallbackComponent,
  showPlaceholder = true,
  hideOnError = false,
  retryOnError = true,
  retryDelay = 1000,
  maxRetries = 2,
  showLoadingState = false,
  loadingComponent,
  placeholderClassName = '',
  containerClassName = '',
  onLoadSuccess,
  onLoadError,
  className = '',
  style,
  ...imgProps
}) => {
  const { imgProps: fallbackProps, hasError, isLoading } = useImageFallback(src, {
    retryOnError,
    retryDelay,
    maxRetries,
    onLoadSuccess,
    onLoadError,
  });

  // Determine final src (original or fallback)
  const finalSrc = hasError && fallbackSrc ? fallbackSrc : src;

  // If error and hideOnError is true, render nothing
  if (hasError && hideOnError && !fallbackSrc) {
    return null;
  }

  // If error and custom fallback component provided
  if (hasError && fallbackComponent && !fallbackSrc) {
    return <>{fallbackComponent}</>;
  }

  // If error and showPlaceholder (default behavior)
  if (hasError && showPlaceholder && !fallbackSrc) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 ${placeholderClassName} ${className}`}
        style={style}
        role="img"
        aria-label={`${alt} (unavailable)`}
      >
        <svg
          className="w-12 h-12 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    );
  }

  // Render loading state if requested
  const LoadingElement = showLoadingState && isLoading && (
    <>
      {loadingComponent || (
        <div
          className={`animate-pulse bg-gray-200 ${className}`}
          style={style}
          aria-label="Loading image"
        />
      )}
    </>
  );

  // Wrapper for loading state
  if (containerClassName || showLoadingState) {
    return (
      <div className={containerClassName}>
        {LoadingElement}
        <img
          src={finalSrc}
          alt={alt}
          className={className}
          style={{
            ...style,
            display: isLoading && showLoadingState ? 'none' : undefined,
          }}
          {...fallbackProps}
          {...imgProps}
        />
      </div>
    );
  }

  // Simple case: just render the img
  return (
    <img
      src={finalSrc}
      alt={alt}
      className={className}
      style={style}
      {...fallbackProps}
      {...imgProps}
    />
  );
};